import React, { useState } from 'react';
import { Eye, Loader2, Sparkles, Download, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { apiClient } from '../../api/client';

const fmtDwell = (s) => {
  s = Math.round(s || 0);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : '—');

const TEMP_STYLE = {
  hot: { bg: '#FEE2E2', color: '#B91C1C', label: '🔥 Hot' },
  warm: { bg: '#FEF3C7', color: '#92400E', label: 'Warm' },
  cool: { bg: '#DBEAFE', color: '#1D4ED8', label: 'Cool' },
  cold: { bg: '#F1F5F9', color: '#475569', label: 'Cold' },
};

export const InvestorInsightPanel = ({ investorId, investorName }) => {
  const [open, setOpen] = useState(false);
  const [insight, setInsight] = useState(null);
  const [read, setRead] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (!insight) {
      setBusy('insight');
      try { setInsight(await apiClient.request(`/api/vault/investors/${investorId}/portal-insight`)); }
      catch (e) { setErr(e.message || 'Failed to load activity'); }
      setBusy('');
    }
  };

  const runAiRead = async (force = false) => {
    setBusy('ai'); setErr('');
    try { setRead(await apiClient.request(`/api/vault/investors/${investorId}/ai-read${force ? '?force=true' : ''}`, { method: 'POST' })); }
    catch (e) { setErr(e.message || 'AI read failed'); }
    setBusy('');
  };

  const p = insight?.portal || {};
  const eng = insight?.engagement || {};
  const temp = TEMP_STYLE[read?.temperature] || TEMP_STYLE.cool;

  return (
    <div className="mt-2" data-testid={`insight-section-${investorId}`}>
      <button onClick={toggle}
        className="w-full py-2 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 hover:bg-indigo-100"
        data-testid={`insight-toggle-${investorId}`}>
        <Eye size={11} /> Vault Activity &amp; AI Read {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {open && (
        <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3 mt-1.5 space-y-3" data-testid={`insight-panel-${investorId}`}>
          {busy === 'insight' && <div className="flex justify-center py-3"><Loader2 size={16} className="animate-spin text-indigo-500" /></div>}
          {err && <p className="text-xs text-red-600" data-testid={`insight-error-${investorId}`}>{err}</p>}

          {insight && (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white rounded-lg p-2" data-testid={`insight-visits-${investorId}`}>
                  <p className="text-base font-black text-gray-900">{p.access_count || 0}</p>
                  <p className="text-[9px] uppercase tracking-wider text-gray-500">Portal logins</p>
                </div>
                <div className="bg-white rounded-lg p-2">
                  <p className="text-base font-black text-gray-900">{fmtDwell(eng.total_seconds)}</p>
                  <p className="text-[9px] uppercase tracking-wider text-gray-500">Time in vault</p>
                </div>
                <div className="bg-white rounded-lg p-2">
                  <p className="text-base font-black text-gray-900">{fmtDate(p.last_access_at)}</p>
                  <p className="text-[9px] uppercase tracking-wider text-gray-500">Last login</p>
                </div>
              </div>

              {(eng.by_tab || []).length > 0 && (
                <div data-testid={`insight-tabs-${investorId}`}>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-indigo-700 mb-1">What they look at</p>
                  {eng.by_tab.slice(0, 5).map(t => (
                    <div key={t.tab} className="flex items-center justify-between text-xs py-0.5">
                      <span className="text-gray-700 capitalize">{t.tab.replace(/-/g, ' ')}</span>
                      <span className="text-gray-500">{t.visits}× · <b className="text-gray-800">{fmtDwell(t.seconds)}</b></span>
                    </div>
                  ))}
                </div>
              )}

              {(insight.dataroom_activity || []).length > 0 && (
                <div data-testid={`insight-activity-${investorId}`}>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-indigo-700 mb-1">Data-room activity &amp; downloads</p>
                  <div className="max-h-24 overflow-y-auto space-y-0.5">
                    {insight.dataroom_activity.slice(0, 8).map((a, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[11px] text-gray-600">
                        {String(a.action).includes('download') ? <Download size={9} className="text-emerald-600 shrink-0" /> : <Eye size={9} className="text-gray-400 shrink-0" />}
                        <span className="truncate">{String(a.action).replace(/_/g, ' ')}{a.detail ? ` — ${a.detail}` : ''}</span>
                        <span className="ml-auto text-gray-400 shrink-0">{fmtDate(a.at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(eng.by_tab || []).length === 0 && (insight.dataroom_activity || []).length === 0 && (
                <p className="text-[11px] text-gray-500 italic">No vault activity recorded yet for {investorName}.</p>
              )}

              {(insight.nudge_history || []).length > 0 && (
                <div data-testid={`nudge-history-${investorId}`}>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Warming history — radar nudges sent</p>
                  <div className="space-y-0.5">
                    {insight.nudge_history.map((n, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[11px]" data-testid={`nudge-row-${investorId}-${i}`}>
                        <span className="text-emerald-600 font-black">▲</span>
                        <span className="text-gray-700 font-semibold">{fmtDate(n.sent_at)}</span>
                        <span className="text-gray-500">· {fmtDwell(n.secs_7d)} in vault that week</span>
                        {n.temperature && (
                          <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                            style={{ backgroundColor: (TEMP_STYLE[n.temperature] || TEMP_STYLE.cool).bg, color: (TEMP_STYLE[n.temperature] || TEMP_STYLE.cool).color }}>
                            {(TEMP_STYLE[n.temperature] || TEMP_STYLE.cool).label}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Read */}
              {!read ? (
                <button onClick={() => runAiRead(false)} disabled={busy === 'ai'}
                  className="w-full py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 hover:opacity-90 disabled:opacity-60"
                  data-testid={`ai-read-btn-${investorId}`}>
                  {busy === 'ai' ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {busy === 'ai' ? 'Claude is reading the signals…' : 'AI Read — what are they thinking?'}
                </button>
              ) : (
                <div className="bg-white border border-violet-200 rounded-lg p-3 space-y-2" data-testid={`ai-read-panel-${investorId}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-700"><Sparkles size={10} /> Claude's read</span>
                    <div className="flex items-center gap-1.5">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: temp.bg, color: temp.color }} data-testid={`ai-temp-${investorId}`}>{temp.label}</span>
                      <button onClick={() => runAiRead(true)} disabled={busy === 'ai'} title="Re-run fresh read" className="text-violet-500 hover:text-violet-700" data-testid={`ai-refresh-${investorId}`}>
                        <RefreshCw size={11} className={busy === 'ai' ? 'animate-spin' : ''} />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-800 leading-relaxed" data-testid={`ai-reading-${investorId}`}>{read.reading}</p>
                  {(read.signals || []).length > 0 && (
                    <ul className="text-[11px] text-gray-600 space-y-0.5 list-disc pl-4">
                      {read.signals.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  )}
                  {(read.recommended_actions || []).length > 0 && (
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Your next moves</p>
                      <ul className="text-[11px] text-gray-800 space-y-1" data-testid={`ai-actions-${investorId}`}>
                        {read.recommended_actions.map((a, i) => (
                          <li key={i} className="flex gap-1.5"><span className="text-emerald-600 font-black shrink-0">{i + 1}.</span><span>{a}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-[9px] text-gray-400">{read.cached ? 'Cached read (refreshes every 6h) — ' : ''}Generated {fmtDate(read.generated_at)} · claude-sonnet-4-6</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default InvestorInsightPanel;
